import { Theme, css } from '@emotion/react';

import { GetServerSideProps, NextPage } from 'next';
import { useTranslation } from 'next-i18next';

import { captureException } from '@sentry/nextjs';
import { Spacing } from 'chatie-design-system';
import fs from 'fs';
import path from 'path';

import Header from '@components/layout/Header';
import Layout from '@components/layout/Layout';

import { withAuth } from '@contexts/auth';

const containerCSS = (theme: Theme) => css`
  gap: ${Spacing.large}px;
  padding: ${Spacing.large}px ${Spacing.medium}px;
  color: ${theme.var.black};

  h2 {
    margin-top: ${Spacing.xlarge}px;
    margin-bottom: ${Spacing.xlarge}px;
  }

  h3 {
    margin: 32px 0 ${Spacing.medium}px;
  }

  div {
    padding-left: ${Spacing.small}px;
  }

  p {
    margin-bottom: ${Spacing.xsmall}px;
  }

  strong {
    display: block;
    margin-bottom: ${Spacing.medium}px;
  }

  p + ul {
    margin-left: 36px;
  }

  ul {
    list-style-position: outside;
    margin-left: ${Spacing.large}px;
  }

  ul,
  ol,
  li {
    list-style: inherit;
    margin-bottom: ${Spacing.medium}px;
  }

  ol {
    list-style-position: outside;
    list-style-type: decimal;
    margin-left: ${Spacing.large}px;
  }

  li + li {
    margin-top: ${Spacing.xxsmall}px;
  }

  li ul,
  li ol {
    margin-top: ${Spacing.xxsmall}px;
    margin-bottom: 0;
  }

  li ul {
    list-style-type: circle;
  }

  li ol {
    list-style-type: decimal;
  }

  a {
    color: ${theme.colors.main300};
    text-decoration: underline;
  }

  table {
    margin: ${Spacing.xxlarge}px 0 ${Spacing.xsmall}px;
  }

  table,
  table th,
  table td {
    border: 1px solid black;
    border-collapse: collapse;
    padding: ${Spacing.xsmall}px;
  }

  table td {
    vertical-align: middle;
  }
`;

interface Props {
  termsOfServiceHTML: string;
}

const TermsPage: NextPage<{ termsOfServiceHTML: string; fileName: string }> = ({
  termsOfServiceHTML,
  fileName,
}) => {
  const { t } = useTranslation();

  const getTitle = () => {
    switch (fileName) {
      case 'terms':
        return '서비스 이용 약관';
      case 'privacy':
        return '개인정보 처리방침';
      case 'collection_agreement':
        return '개인정보 수집 및 이용 동의';
      default:
        break;
    }
  };

  return (
    <Layout>
      <Header title={t(getTitle())} />
      <div
        css={containerCSS}
        dangerouslySetInnerHTML={{ __html: termsOfServiceHTML }}
      ></div>
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async ({
  params,
  query,
}) => {
  let file = (params?.file as string) || 'terms';
  if (file === 'collection_agreement') {
    file = 'privacy';
  }
  const latestVersion =
    file === 'privacy'
      ? '1.5'
      : file === 'collection_agreement'
      ? '1.0'
      : '1.5';
  const version = (query?.version as string) || latestVersion;

  const filePath = path.join(
    process.cwd(),
    'public/policy/terms',
    `${file}-v${version}.html`
  );

  let termsOfServiceHTML;
  try {
    termsOfServiceHTML = fs.readFileSync(filePath, 'utf8');
  } catch (error) {
    captureException(error);
    return { notFound: true };
  }

  return { props: { termsOfServiceHTML, fileName: file } };
};

export default withAuth(TermsPage, { isSignInRequired: false });
